import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import "dayjs/locale/en-gb";
import { updateUserProfile } from "../features/userProfile/userProfileSlice";
import { getActiveClubs } from "../features/clubs/clubsService";
import { getProfileCompletion, GENDERS } from "../utils/profileCompletion";
import { useAccessControl } from "../context/AccessControlContext";

const validationSchema = yup.object().shape({
  firstName: yup.string().required("First name is required").min(2, "Too short"),
  lastName: yup.string().required("Last name is required").min(2, "Too short"),
  phoneNumber: yup
    .string()
    .required("Phone is required")
    .matches(/^[+\d\s()-]{7,20}$/, "Enter a valid phone number"),
  gender: yup.string().required("Gender is required").oneOf(GENDERS),
  homeClubId: yup.string().required("Home club is required"),
  bsaNumber: yup
    .string()
    .required("BSA number is required")
    .matches(/^\d+$/, "Digits only")
    .max(10, "Max 10 digits"),
  // Stays a STRING schema: yup.date() would coerce the value into a Date
  // object, which then serialises to an ISO datetime and trips the server's
  // strict YYYY-MM-DD check.
  dob: yup
    .string()
    .required("Date of birth is required")
    .test("valid-date", "Enter a valid date", (value) => {
      if (!value) return true;
      return !Number.isNaN(new Date(`${value}T00:00:00`).getTime());
    })
    .test("not-future", "Date of birth cannot be in the future", (value) => {
      if (!value) return true;
      return new Date(`${value}T00:00:00`) <= new Date();
    }),
});

/**
 * The "complete your profile" popup.
 *
 * Auto-opens once per session from the header when the profile is incomplete;
 * the header's progress strip also opens it on demand. Saves through the same
 * updateUserProfile thunk the rest of the app uses, so the header recomputes
 * completion from the store when the save lands.
 */
export default function ProfileCompletionDialog({ open, onClose }) {
  const dispatch = useDispatch();
  const { user } = useKindeAuth();
  const { isAdmin } = useAccessControl();
  const profile = useSelector((state) => state.userProfile.profile);
  const isSaving = useSelector((state) => state.userProfile.isLoading);

  const [clubs, setClubs] = useState([]);
  const [submitError, setSubmitError] = useState("");

  // Load the club list whenever the dialog opens.
  useEffect(() => {
    if (!open) return;

    getActiveClubs()
      .then((response) => setClubs(response.data.clubs || []))
      .catch(() => setClubs([]));
  }, [open]);

  const defaults = useMemo(
    () => ({
      firstName: profile?.first_name || "",
      lastName: profile?.last_name || "",
      phoneNumber: profile?.phone_number || "",
      gender: profile?.gender || "",
      homeClubId: profile?.home_club_id ? String(profile.home_club_id) : "",
      bsaNumber: profile?.bsa_number || "",
      emailNotifications: profile?.email_notifications ?? true,
      dob: profile?.date_of_birth || "",
    }),
    [profile],
  );

  const { register, handleSubmit, control, watch, formState } = useForm({
    resolver: yupResolver(validationSchema),
    mode: "onBlur",
    values: defaults,
  });
  const { errors } = formState;

  // Live completion, so the bar moves as fields are filled rather than only
  // after saving.
  const watched = watch();
  const liveProfile = {
    ...profile,
    first_name: watched.firstName,
    last_name: watched.lastName,
    phone_number: watched.phoneNumber,
    gender: watched.gender,
    home_club_id: watched.homeClubId ? Number(watched.homeClubId) : null,
    bsa_number: watched.bsaNumber,
  };
  const completion = getProfileCompletion(liveProfile, user);
  const emailValue = profile?.email || user?.email || "";

  const onSubmit = async (data) => {
    setSubmitError("");

    try {
      await dispatch(
        updateUserProfile({
          first_name: data.firstName.trim(),
          last_name: data.lastName.trim(),
          phone_number: data.phoneNumber.trim(),
          gender: data.gender,
          home_club_id: data.homeClubId ? Number(data.homeClubId) : null,
          bsa_number: data.bsaNumber.trim(),
          email_notifications: Boolean(data.emailNotifications),
          // Slice defensively: if anything ever hands us an ISO datetime,
          // only the date part goes to the server.
          date_of_birth: data.dob ? String(data.dob).slice(0, 10) : null,
        }),
      ).unwrap();
      onClose();
    } catch (error) {
      // The thunk already raised the alert; keep the dialog open so the
      // visitor can fix the field rather than losing their input.
      setSubmitError(
        error?.error || error?.message || "Could not save. Please try again.",
      );
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Complete your profile</DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Box>
            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
              <Typography variant="body2" color="text.secondary">
                Profile completion
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {completion.pct}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={completion.pct}
              sx={{ height: 8, borderRadius: 4 }}
            />
          </Box>

          {submitError && (
            <Typography variant="body2" color="error">
              {submitError}
            </Typography>
          )}

          <Stack direction="row" spacing={2}>
            <TextField
              {...register("firstName")}
              label="First name"
              fullWidth
              error={!!errors.firstName}
              helperText={errors.firstName?.message}
            />
            <TextField
              {...register("lastName")}
              label="Last name"
              fullWidth
              error={!!errors.lastName}
              helperText={errors.lastName?.message}
            />
          </Stack>

          <Stack direction="row" spacing={2}>
            <TextField
              {...register("phoneNumber")}
              label="Phone"
              fullWidth
              error={!!errors.phoneNumber}
              helperText={errors.phoneNumber?.message}
            />
            <TextField
              {...register("bsaNumber")}
              label="BSA number"
              fullWidth
              error={!!errors.bsaNumber}
              helperText={errors.bsaNumber?.message}
            />
          </Stack>

          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
            <Controller
              name="dob"
              control={control}
              render={({ field }) => (
                <DatePicker
                  label="Date of birth"
                  format="DD/MM/YYYY"
                  value={field.value ? dayjs(field.value) : null}
                  onChange={(date) =>
                    field.onChange(date ? date.format("YYYY-MM-DD") : "")
                  }
                  disableFuture
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!errors.dob,
                      helperText: errors.dob?.message,
                    },
                  }}
                />
              )}
            />
          </LocalizationProvider>

          <Stack direction="row" spacing={2}>
            <Controller
              name="gender"
              control={control}
              render={({ field }) => (
                <Box sx={{ width: "100%" }}>
                  <Select
                    {...field}
                    fullWidth
                    displayEmpty
                    error={!!errors.gender}
                    renderValue={(selected) =>
                      selected ? (
                        selected
                      ) : (
                        <Typography color="text.secondary">Gender</Typography>
                      )
                    }
                  >
                    <MenuItem value="" disabled>
                      Select
                    </MenuItem>
                    {GENDERS.map((gender) => (
                      <MenuItem key={gender} value={gender}>
                        {gender}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.gender && (
                    <Typography variant="caption" color="error">
                      {errors.gender.message}
                    </Typography>
                  )}
                </Box>
              )}
            />

            <Controller
              name="homeClubId"
              control={control}
              render={({ field }) => {
                const selected =
                  clubs.find((club) => String(club.id) === String(field.value)) ||
                  null;

                return (
                  <Box sx={{ width: "100%" }}>
                    <Autocomplete
                      options={clubs}
                      getOptionLabel={(club) => club.name}
                      groupBy={(club) =>
                        club.district_name || club.district_id || "Other"
                      }
                      value={selected}
                      onChange={(_, club) =>
                        field.onChange(club ? String(club.id) : "")
                      }
                      isOptionEqualToValue={(option, value) =>
                        option.id === value.id
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Home club"
                          placeholder="Search clubs..."
                          error={!!errors.homeClubId}
                          helperText={
                            errors.homeClubId?.message ||
                            (clubs.length === 0
                              ? "No clubs available yet"
                              : undefined)
                          }
                        />
                      )}
                      noOptionsText="No clubs match"
                    />
                  </Box>
                );
              }}
            />
          </Stack>

          <TextField
            label="Email"
            value={emailValue}
            disabled
            fullWidth
            helperText="Your login email. Change it in your account settings."
          />

          {isAdmin && (
            <Controller
              name="emailNotifications"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(field.value)}
                      onChange={(event) => field.onChange(event.target.checked)}
                    />
                  }
                  label="Email me when a new entry flag is submitted"
                />
              )}
            />
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isSaving} color="inherit">
          Not now
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit(onSubmit)}
          disabled={isSaving}
        >
          {isSaving ? "Saving..." : "Save profile"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
